import { useEffect, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { getAppStore, setAppStore, useAppStore } from '@/store';
import { generationPipeline } from '@/utils/user/generationPipeline';
import { getProjectJsonUrl } from '@/utils/project/getProjectJsonUrl';
import { setShouldSkipAutoGeneration } from '@/utils/storage/autoGeneration';
import { submitWebHook } from '@/requests/magic-flow/submitWebHook';
import { getMediaFromFlowId } from '@/requests/magic-flow/getMediaFromFlowId';
import { buildParamsFromInputs } from '@/utils/flow/buildParamsFromInputs';
import { createContentFile } from '@/utils/media/createContentFile';
import { createNotificationConfig } from '@/utils/ui/createNotificationConfig';
import { useAuth } from './useAuth';

import type { IAppStore } from '@/store';

const selector = (state: IAppStore) => ({
  selectedPreset: state.selectedPreset,
  areCreditsAvailable: state.areCreditsAvailable,
  shouldSkipAutoGeneration: state.shouldSkipAutoGeneration,
  inputs: state.inputs,
  toolId: state.toolId,
  folderName: state.folderName,
});

const useAutoGeneration = () => {
  const { isAuthorized } = useAuth();
  const {
    selectedPreset,
    areCreditsAvailable,
    shouldSkipAutoGeneration,
    inputs,
    toolId,
    folderName,
  } = useAppStore(useShallow(selector));

  const previousCreditsRef = useRef<boolean | null>(null);
  const autoGenerationInProgressRef = useRef(false);
  const { jsonUrl } = getProjectJsonUrl();
  const isDeeplinkSession = Boolean(jsonUrl);
  const [isAutoGenerationPending, setIsAutoGenerationPending] =
    useState(isDeeplinkSession);

  useEffect(() => {
    if (!isDeeplinkSession || shouldSkipAutoGeneration) {
      setIsAutoGenerationPending(false);
      return;
    }

    // Wait for authorization state to be determined
    if (!isAuthorized) {
      setIsAutoGenerationPending(false);
      return;
    }

    const { isPrimaryActionLoading } = getAppStore();
    const webhookId = selectedPreset?.flowId;

    const creditsBecameAvailable =
      previousCreditsRef.current === false && areCreditsAvailable === true;

    if (areCreditsAvailable !== null) {
      previousCreditsRef.current = areCreditsAvailable;
    }

    const shouldSkipAutoGenerationFlag =
      !webhookId || isPrimaryActionLoading || areCreditsAvailable === false;

    if (
      (shouldSkipAutoGenerationFlag && !creditsBecameAvailable) ||
      autoGenerationInProgressRef.current
    ) {
      setIsAutoGenerationPending(false);
      return;
    }
    const triggerAutoGeneration = async () => {
      // webhookId is guaranteed to exist at this point due to earlier check
      if (!webhookId) return;

      try {
        autoGenerationInProgressRef.current = true;
        setAppStore({
          isPrimaryActionLoading: true,
          isGenerating: true,
        });
        setIsAutoGenerationPending(false);

        const result = await generationPipeline({ webhookId });

        if (!result?.canProcess) {
          autoGenerationInProgressRef.current = false;
          setAppStore({
            screen: 'create',
            isGenerating: false,
          });
          return;
        }

        setAppStore({
          resultFile: null,
          projectCreditsCount: result.projectCreditsCount ?? null,
          screen: 'loading',
        });

        // Build params and submit webhook
        const params = buildParamsFromInputs(inputs);
        const webhookResponse = await submitWebHook({
          webhookId,
          params,
          driveOptions: {
            folderName,
            packageId: toolId,
          },
          notificationConfig: createNotificationConfig(),
        });

        // Poll for result
        const flowMedia = await getMediaFromFlowId(webhookResponse.id);
        if (!flowMedia) {
          throw new Error('Failed to get generation result');
        }

        const { contentFile } = createContentFile({
          media: flowMedia,
          fileName: selectedPreset?.title ?? 'Generated',
        });

        setAppStore({
          resultFile: flowMedia,
          screen: 'result',
        });

        getAppStore().addContentFile(contentFile);

        if (jsonUrl) {
          await setShouldSkipAutoGeneration(jsonUrl);
        }
        setAppStore({ shouldSkipAutoGeneration: true });
      } catch (error) {
        console.error('[useAutoGeneration] Auto generation failed:', error);
        setAppStore({
          screen: 'create',
          isGenerating: false,
        });
        setIsAutoGenerationPending(false);
        autoGenerationInProgressRef.current = false;
        if (jsonUrl) {
          await setShouldSkipAutoGeneration(jsonUrl);
        }
        setAppStore({ shouldSkipAutoGeneration: true });
      } finally {
        setAppStore({
          isPrimaryActionLoading: false,
          isGenerating: false,
        });
      }
    };

    triggerAutoGeneration();
  }, [
    areCreditsAvailable,
    isAuthorized,
    isDeeplinkSession,
    selectedPreset?.flowId,
    selectedPreset?.title,
    jsonUrl,
    inputs,
    toolId,
    folderName,
    shouldSkipAutoGeneration,
  ]);

  return { canAutoGenerate: isAutoGenerationPending };
};

export { useAutoGeneration };
